import path from 'ramda/src/path';
import { createImmutableSelector } from 'invision-core/src/utilities/create.immutable.selector';
import { MetadataCodeTypeDictionarySelector, MetadataCodeTypeSelector } from 'invision-core/src/components/metadata/codes/codes.selectors';
import { PageSizePreferenceSelector } from 'invision-core/src/components/session/session.selectors';
import { CODES } from 'invision-core/src/components/metadata/codes/codes.constants';
import i18n from 'invision-core/src/components/i18n/i18n';
import localeKeys from '../../locales/keys';
import { convertUnitOfMeasure, getUnitOfMeasure } from './shared.entitlements.selectors.helper';
import { ENTITLEMENT_SHARE_STATUS } from '../../customercare.constants';
import { EMPTY_OBJECT, EMPTY_ARRAY } from '../constants/common.constants';
import { getFormattedServiceAttributeValue } from './services.list.selectors.helper';
var sharedEntitlementsData = function sharedEntitlementsData(state) {
  return state.customercare.customer.selectedCustomer.sharedEntitlements;
};
export var entitlementsSelector = createImmutableSelector([sharedEntitlementsData], function (sharedEntilements) {
  return sharedEntilements.entitlements;
});
export var entitlementsPaginationDataSelector = createImmutableSelector([sharedEntitlementsData, PageSizePreferenceSelector], function (sharedEntilements, defaultPageSize) {
  if (!sharedEntilements.entitlementsPaginationData.pageSize) {
    return Object.assign({}, sharedEntilements.entitlementsPaginationData, {
      pageSize: defaultPageSize
    });
  }
  return sharedEntilements.entitlementsPaginationData;
});
var recoverableUIStateSelector = function recoverableUIStateSelector(state) {
  if (state.customercare.recoverableUiState) {
    return state.customercare.recoverableUiState.sharedEntitlements;
  }
};
export var sharedEntitlementsPageSizeSelector = createImmutableSelector([recoverableUIStateSelector, PageSizePreferenceSelector], function (uiState, defaultPageSize) {
  return uiState.pageSize ? uiState.pageSize : defaultPageSize;
});
export var currentPageNumberSelector = createImmutableSelector([recoverableUIStateSelector], function (uiState) {
  return uiState.pageNumber;
});
export var entitlementRecordCountSelector = createImmutableSelector([sharedEntitlementsData], function (sharedEntilements) {
  return sharedEntilements.recordCount;
});
export var unitsOfMeasureSelector = createImmutableSelector([MetadataCodeTypeDictionarySelector(CODES.UnitsOfMeasure)], function (unitsOfMeasure) {
  return Object.values(unitsOfMeasure);
});
export var selectedEntitlementIndexSelector = createImmutableSelector([sharedEntitlementsData], function (sharedEntitlements) {
  return sharedEntitlements.selectedEntitlementIndex;
});
export var isFetchingEntitlementUsageSelector = createImmutableSelector([sharedEntitlementsData], function (entitlementsData) {
  return entitlementsData.isFetchingEntitlementUsage;
});
export var isFetchingEntitlementsSelector = createImmutableSelector([sharedEntitlementsData], function (entitlementsData) {
  return entitlementsData.isFetchingEntitlements;
});
export var isUpdatingEntitlementShareStatusSelector = createImmutableSelector([sharedEntitlementsData], function (entitlementsData) {
  return entitlementsData.isUpdatingEntitlementShareStatus;
});
export var entitlementUsageTotalSelector = createImmutableSelector([sharedEntitlementsData], function (entitlementsData) {
  return entitlementsData.entitlementUsageTotal;
});
export var entitlementUsagesSelector = createImmutableSelector([sharedEntitlementsData], function (entitlementsData) {
  return entitlementsData.entitlementUsages;
});
export var formattedEntitlementsSelector = createImmutableSelector([entitlementsSelector, unitsOfMeasureSelector, MetadataCodeTypeDictionarySelector(CODES.ServiceAttribute), MetadataCodeTypeDictionarySelector(CODES.RegularExpression)], function (entitlements, unitsOfMeasure, serviceAttributes, regularExpressions) {
  return entitlements.map(function (entitlement) {
    var owningService = entitlement.EntitlementIdentifier && entitlement.EntitlementIdentifier.OwningServiceIdentifier;
    if (owningService) {
      entitlement = entitlement.setIn(['EntitlementIdentifier', 'OwningServiceIdentifier', 'FormattedValue'], getFormattedServiceAttributeValue(owningService.ServiceAttributeId, owningService.Value, serviceAttributes, regularExpressions));
    }
    return Object.assign({}, entitlement, {
      ShareAction: i18n.translate(entitlement.SharingStatus === ENTITLEMENT_SHARE_STATUS.DISABLED ? localeKeys.SHARING_ACTIONS.START_SHARING : localeKeys.SHARING_ACTIONS.STOP_SHARING),
      UnitOfMeasure: getUnitOfMeasure(entitlement.BalanceUnitCode, unitsOfMeasure)
    });
  });
});
export var selectedEntitlementSelector = createImmutableSelector([selectedEntitlementIndexSelector, formattedEntitlementsSelector], function (selectedEntitlementIndex, entitlements) {
  return entitlements[selectedEntitlementIndex] || EMPTY_OBJECT;
});
export var entitlementUsagesWithFormattedServiceAttributeSelector = createImmutableSelector([entitlementUsagesSelector, MetadataCodeTypeDictionarySelector(CODES.ServiceAttribute), MetadataCodeTypeDictionarySelector(CODES.RegularExpression)], function (entitlementUsages, serviceAttributes, regularExpressions) {
  return (entitlementUsages || EMPTY_ARRAY).map(function (service) {
    return service.ServiceIdentifier ? service.setIn(['ServiceIdentifier', 'FormattedValue'], getFormattedServiceAttributeValue(service.ServiceIdentifier.ServiceAttributeId, service.ServiceIdentifier.Value, serviceAttributes, regularExpressions)) : service;
  });
});
export var entitlementUsagesTableDataSelector = createImmutableSelector([entitlementUsagesWithFormattedServiceAttributeSelector, selectedEntitlementSelector, MetadataCodeTypeDictionarySelector(CODES.UnitsOfMeasure), MetadataCodeTypeDictionarySelector(CODES.UsageUnitConversion), selectedEntitlementIndexSelector], function (entitlementUsages, selectedEntitlement, unitsOfMeasureDictionary, unitConversionTable, selectedEntitlementIndex) {
  var ownerServiceId = path(['EntitlementIdentifier', 'OwningServiceIdentifier', 'ServiceAttributeId'], selectedEntitlement);
  return entitlementUsages.reduce(function (usages, service) {
    service.EntitlementUsageTotals.forEach(function (usage) {
      usages.push({
        entitlementId: path(['EntitlementIdentifier', 'EntitlementId'], usage),
        entitlementName: usage.EntitlementName,
        friendlyName: service.ServiceIdentifier && service.ServiceIdentifier.FriendlyName,
        groupCode: path(['EntitlementIdentifier', 'GroupCode'], selectedEntitlement),
        selectedEntitlementIndex: selectedEntitlementIndex,
        subscriberId: service.SubscriberId,
        ownerLabel: !!ownerServiceId && ownerServiceId === (service.ServiceIdentifier && service.ServiceIdentifier.ServiceAttributeId) ? i18n.translate(localeKeys.OWNER).toUpperCase() : '',
        serviceAttributeId: service.ServiceIdentifier && service.ServiceIdentifier.ServiceAttributeId,
        serviceId: service.ServiceIdentifier && service.ServiceIdentifier.Value,
        serviceIdFormatted: service.ServiceIdentifier && service.ServiceIdentifier.FormattedValue,
        transactionId: service.TransactionId,
        usage: {
          balanceUsed: convertUnitOfMeasure(usage.TotalUsageAmount, usage.UnitOfMeasureCode, selectedEntitlement.BalanceUnitCode, unitConversionTable),
          initialBalance: selectedEntitlement.InitialBalance,
          initialBalanceUnit: selectedEntitlement.UnitDisplayName && unitsOfMeasureDictionary[selectedEntitlement.BalanceUnitCode].Name === 'Units' ? selectedEntitlement.UnitDisplayName : path(['AdditionalProperties', 'short_name'], unitsOfMeasureDictionary[selectedEntitlement.BalanceUnitCode]) || ''
        }
      });
    });
    return usages;
  }, []);
});